import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Bookmark, Download, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";

const SavedAnswers = () => {
  const { token } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [questionsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/questions/history?saved_only=true`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contracts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setQuestions(questionsRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      toast.error("Failed to load saved answers");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (questionId) => {
    try {
      await axios.delete(`${API}/questions/${questionId}/save`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(questions.filter(q => q.id !== questionId));
      toast.success("Removed from saved");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const handleExport = async (questionId) => {
    try {
      // Get the token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      // Use fetch API for proper blob handling
      const response = await fetch(`${API}/questions/${questionId}/export-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Check response status
      if (!response.ok) {
        const errorText = await response.text();
        toast.error("Failed to download PDF");
        console.error("PDF export failed:", response.status, errorText);
        return;
      }

      // Validate Content-Type
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        const responseText = await response.text();
        toast.error("Server did not return a PDF file");
        console.error("Non-PDF response:", responseText);
        return;
      }

      // Convert to blob
      const blob = await response.blob();
      
      // Validate blob size
      if (blob.size < 1000) {
        toast.error("PDF file is too small or corrupted");
        return;
      }

      // Extract filename
      let filename = 'CBAIQ_Answer.pdf';
      const disposition = response.headers.get('content-disposition');
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success("PDF download started");
    } catch (error) {
      toast.error("Failed to download PDF. Please try again.");
      console.error("PDF export error:", error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getContractName = (contractId) => {
    const contract = contracts.find(c => c.id === contractId);
    return contract?.name || "Unknown Contract";
  };

  const getRiskClass = (level) => {
    const lower = level?.toLowerCase();
    if (lower === "low") return "risk-low";
    if (lower === "medium") return "risk-medium";
    if (lower === "high") return "risk-high";
    return "bg-secondary text-muted-foreground";
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2" data-testid="saved-title">
            Saved Answers
          </h1>
          <p className="text-muted-foreground">
            Your bookmarked answers for quick reference
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="h-32 skeleton" />
            ))}
          </div>
        ) : questions.length === 0 ? (
          <div className="empty-state bg-card border border-border rounded-md">
            <Bookmark className="empty-state-icon" />
            <h3 className="empty-state-title">No saved answers</h3>
            <p className="empty-state-description">
              Save answers from your questions to access them quickly later
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((q) => (
              <div
                key={q.id}
                className="bg-card border border-border rounded-md overflow-hidden"
                data-testid={`saved-item-${q.id}`}
              >
                <div
                  className="p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium mb-2">{q.question}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className={`risk-badge ${getRiskClass(q.risk_level)}`}>
                          {q.risk_level} Risk
                        </span>
                        <span>•</span>
                        <span>{getContractName(q.contract_id)}</span>
                        <span>•</span>
                        <span>{formatDate(q.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExport(q.id);
                        }}
                        data-testid={`export-btn-${q.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove(q.id);
                        }}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`remove-btn-${q.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {expandedId === q.id && (
                  <div className="border-t border-border p-4 bg-secondary/20">
                    {q.answer_sections.map((section, i) => (
                      <div key={`${q.id}-section-${section.title}-${i}`} className="mb-4 last:mb-0">
                        <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-1">
                          {section.title}
                        </h4>
                        <p className="text-sm whitespace-pre-line">{section.content}</p>
                      </div>
                    ))}
                    
                    {q.citations && q.citations.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">
                          Citations
                        </h4>
                        {q.citations.map((citation, i) => (
                          <div key={`${q.id}-cite-${i}`} className="citation-block citation-text mb-2">
                            {citation}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SavedAnswers;
